/**
 * Page Transitions Component
 * Handles scroll-triggered fade-in animations for elements with data-animate attributes
 * Uses Intersection Observer API for performance-efficient scroll detection
 *
 * Usage: Simply add data-animate attribute to any element (fade-in is the default animation)
 *
 * IMPORTANT: This needs to be in HTML <head> for no-JS fallback:
 * <noscript><style>[data-animate]{opacity:1!important;}</style></noscript>
 *
 * @example
 * // Basic usage - fade in when scrolled into view
 * <div data-animate>Content</div>
 *
 * @example
 * // With delay
 * <div data-animate data-animate-delay="short">Content</div>
 * <div data-animate data-animate-delay="300ms">Content</div>
 *
 * @example
 * // Staggered animations
 * <div data-animate data-animate-delay="0">Item 1</div>
 * <div data-animate data-animate-delay="150ms">Item 2</div>
 * <div data-animate data-animate-delay="300ms">Item 3</div>
 */

interface AnimateElement extends Element {
  dataset: DOMStringMap & {
    animate?: string;
    animateDelay?: string;
  };
}

class PageTransitions {
  private observer: IntersectionObserver | null = null;
  private largeElementObserver: IntersectionObserver | null = null;
  private elements: AnimateElement[] = [];
  private observerOptions!: IntersectionObserverInit;

  constructor() {
    this.init();
  }

  /**
   * Initialize the page transitions
   */
  private init(): void {
    // Check if browser supports Intersection Observer
    if (!('IntersectionObserver' in globalThis)) {
      console.warn('Intersection Observer not supported. Animations will be disabled.');
      this.fallback();
      return;
    }

    // Find all elements with data-animate attribute
    this.elements = Array.from(document.querySelectorAll<AnimateElement>('[data-animate]'));

    if (this.elements.length === 0) {
      return;
    }

    this.observerOptions = this.getObserverOptions();

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    // Create the standard Intersection Observer
    this.observer = new IntersectionObserver(this.handleIntersection.bind(this), this.observerOptions);

    // Large elements (taller than the viewport) can never reach the standard threshold
    // (e.g. a 10000px element in an 800px viewport has a max ratio of 0.08, below threshold: 0.1).
    // A second observer with threshold: 0 fires as soon as any pixel enters the root.
    const largeElementOptions: IntersectionObserverInit = { ...this.observerOptions, threshold: 0 };
    this.largeElementObserver = new IntersectionObserver(this.handleIntersection.bind(this), largeElementOptions);

    // Observe all animate elements, routing large ones to the dedicated observer
    this.elements.forEach(element => {
      const elementHeight = (element as HTMLElement).offsetHeight || element.getBoundingClientRect().height;
      if (elementHeight > viewportHeight) {
        this.largeElementObserver?.observe(element);
      } else {
        this.observer?.observe(element);
      }
    });

    // Initial check for elements already in view at page load
    requestAnimationFrame(() => {
      const currentViewportHeight = window.innerHeight || document.documentElement.clientHeight;

      this.elements.forEach(element => {
        const rect = element.getBoundingClientRect();

        if (rect.top < currentViewportHeight && rect.bottom > 0) {
          this.animateElement(element);
          this.observer?.unobserve(element);
          this.largeElementObserver?.unobserve(element);
        }
      });
    });
  }

  /**
   * Resolve rootMargin and threshold based on viewport
   * Uses percentage on mobile to scale across devices
   * Uses small threshold on mobile
   */
  private getObserverOptions(): IntersectionObserverInit {
    const isMobile = globalThis.matchMedia('(max-width: 768px)').matches;
    return {
      root: null,
      rootMargin: isMobile ? '0px 0px -20% 0px' : '0px 0px -100px 0px',
      threshold: isMobile ? 0.05 : 0.1,
    };
  }

  /**
   * Handle intersection observer callback
   */
  private handleIntersection(entries: IntersectionObserverEntry[], observer: IntersectionObserver): void {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;

      const element = entry.target as AnimateElement;
      this.animateElement(element);
      observer.unobserve(element);
    });
  }

  /**
   * Animate an element by adding the is-visible class
   */
  private animateElement(element: AnimateElement): void {
    if (element.classList.contains('is-visible')) return;
    // Check if element has a delay attribute
    const delay = element.dataset.animateDelay;

    if (delay) {
      // Parse delay value (supports values like "300ms", "0.3s", or "short", "medium", "long")
      const delayValue = this.parseDelay(delay);

      // Set CSS custom property for delay
      (element as HTMLElement).style.setProperty('--animation-delay', delayValue);

      // Add visible class after delay
      setTimeout(() => {
        element.classList.add('is-visible');
      }, this.getDelayInMs(delayValue));
    } else {
      // Add visible class immediately
      element.classList.add('is-visible');
    }
  }

  /**
   * Parse delay value and return valid CSS time value
   */
  private parseDelay(delay: string): string {
    // Handle named delays
    const namedDelays: Record<string, string> = {
      short: 'var(--animation-delay-short)',
      medium: 'var(--animation-delay-medium)',
      long: 'var(--animation-delay-long)',
    };

    if (namedDelays[delay]) {
      return namedDelays[delay];
    }

    // Handle numeric values (convert to ms if needed)
    if (/^\d+$/.exec(delay)) {
      return `${delay}ms`;
    }

    // Return as-is if it's already in ms or s format
    return delay;
  }

  /**
   * Convert delay value to milliseconds for setTimeout
   */
  private getDelayInMs(delay: string): number {
    // Handle CSS variables
    if (delay.startsWith('var(')) {
      const varNameRegex = /var\(--([^)]+)\)/;
      const varName = varNameRegex.exec(delay)?.[1];
      if (varName) {
        // Get computed value from CSS
        const computedDelay = getComputedStyle(document.documentElement).getPropertyValue(`--${varName}`).trim();
        return this.parseTimeToMs(computedDelay);
      }
      return 0;
    }

    return this.parseTimeToMs(delay);
  }

  /**
   * Parse time string (e.g., "300ms", "0.3s") to milliseconds
   */
  private parseTimeToMs(time: string): number {
    const regex = /^([\d.]+)(ms|s)$/;
    const match = regex.exec(time);
    if (!match) return 0;

    const value = Number.parseFloat(match[1]);
    const unit = match[2];

    return unit === 's' ? value * 1000 : value;
  }

  /**
   * Fallback for browsers without Intersection Observer support
   * Adds is-visible class to all elements immediately
   */
  private fallback(): void {
    this.elements = Array.from(document.querySelectorAll<AnimateElement>('[data-animate]'));

    this.elements.forEach(element => {
      element.classList.add('is-visible');
    });
  }

  /**
   * Manually trigger animation for a specific element
   * Useful for dynamically added content
   */
  public animateElementById(id: string): void {
    const element = document.getElementById(id) as AnimateElement | null;
    if (element?.dataset.animate) {
      this.animateElement(element);
    }
  }

  /**
   * Manually trigger animation for elements matching a selector
   * Useful for dynamically added content
   */
  public animateElements(selector: string): void {
    const elements = Array.from(document.querySelectorAll<AnimateElement>(selector));

    elements.forEach(element => {
      if (element.dataset.animate) {
        this.animateElement(element);
      }
    });
  }

  /**
   * Observe new elements (for dynamically added content)
   */
  public observeNewElements(selector: string = '[data-animate]:not(.is-visible)'): void {
    const newElements = Array.from(document.querySelectorAll<AnimateElement>(selector));

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    newElements.forEach(element => {
      if (!this.elements.includes(element)) {
        this.elements.push(element);
        const elementHeight = (element as HTMLElement).offsetHeight || element.getBoundingClientRect().height;
        if (elementHeight > viewportHeight) {
          this.largeElementObserver?.observe(element);
        } else {
          this.observer?.observe(element);
        }
      }
    });
  }

  /**
   * Reset animations for all elements
   * Useful for testing or re-triggering animations
   */
  public reset(): void {
    if (!this.observer) return;

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    this.elements.forEach(element => {
      element.classList.remove('is-visible');
      (element as HTMLElement).style.removeProperty('--animation-delay');
      const elementHeight = (element as HTMLElement).offsetHeight || element.getBoundingClientRect().height;
      if (elementHeight > viewportHeight) {
        this.largeElementObserver?.observe(element);
      } else {
        this.observer?.observe(element);
      }
    });
  }

  /**
   * Destroy the page transitions instance
   */
  public destroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.largeElementObserver) {
      this.largeElementObserver.disconnect();
      this.largeElementObserver = null;
    }
    this.elements = [];
  }
}

// Initialize page transitions on DOM ready
const pageTransitionsInstance: { current: PageTransitions | null } = { current: null };

const initPageTransitions = (): void => {
  if (!pageTransitionsInstance.current) {
    pageTransitionsInstance.current = new PageTransitions();
  }
};

// Defer initialisation to after the browser has finished critical work.
// The observer setup queries the DOM for every [data-animate] element;
// moving this off the critical path reduces TBT on mobile.
function scheduleInit() {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => initPageTransitions());
  } else {
    setTimeout(initPageTransitions, 0);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleInit);
} else {
  scheduleInit();
}

// Export for use in other modules if needed
export { PageTransitions, pageTransitionsInstance };
